import React, { useState, useEffect } from 'react';
import { PiggyBank, Plus, Trash2, AlertCircle, TrendingUp, TrendingDown, DollarSign, LineChart, Calendar, Target, Percent, Clock, Info } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { SearchInput } from '../common/SearchInput';
import { Badge } from '../common/Badge';

interface RetirementAccount {
  id: string;
  type: string;
  provider: string;
  account_number: string;
  balance: string;
  contributions_ytd: string;
  employer_match?: string;
  investment_strategy?: string;
  target_retirement_date?: string;
  beneficiaries?: string[];
  notes?: string;
  preservation_age?: string;
  tax_file_number?: string;
  member_number?: string;
  insurance_cover?: {
    death?: string;
    tpd?: string;
    income_protection?: string;
  };
  investment_options?: {
    name: string;
    percentage: string;
  }[];
  fees?: {
    admin_fee?: string;
    investment_fee?: string;
    insurance_fee?: string;
  };
}

export function Retirement() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<RetirementAccount[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [formData, setFormData] = useState({
    type: 'superannuation',
    provider: '',
    account_number: '',
    balance: '',
    contributions_ytd: '',
    employer_match: '',
    investment_strategy: '',
    target_retirement_date: '',
    beneficiaries: [''],
    notes: '',
    preservation_age: '',
    tax_file_number: '',
    member_number: '',
    insurance_cover: {
      death: '',
      tpd: '',
      income_protection: ''
    },
    investment_options: [{ name: '', percentage: '' }],
    fees: {
      admin_fee: '',
      investment_fee: '',
      insurance_fee: ''
    }
  });

  const accountTypes = [
    { value: 'superannuation', label: 'Superannuation' },
    { value: 'pension', label: 'Account-Based Pension' },
    { value: 'smsf', label: 'Self-Managed Super Fund' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    if (user) {
      loadAccounts();
    }
  }, [user]);

  useEffect(() => {
    const handler = () => { if (user) loadAccounts(true); };
    window.addEventListener('refresh-financial', handler);
    return () => window.removeEventListener('refresh-financial', handler);
  }, [user]);

  async function loadAccounts(background = false) {
    try {
      if (!background) setLoading(true);
      const { data, error } = await supabase
        .from('financial_accounts')
        .select('*')
        .eq('user_id', user?.id)
        .eq('type', 'retirement');

      if (error) throw error;
      setAccounts(data || []);
    } catch (err) {
      if (!background) setError(err instanceof Error ? err.message : 'Failed to load retirement accounts');
    } finally {
      if (!background) setLoading(false);
    }
  }

  const handleSaveAccount = async () => {
    try {
      const accountData = {
        type: 'retirement',
        institution: formData.provider,
        account_number: formData.account_number,
        balance: formData.balance,
        currency: 'AUD',
        notes: formData.notes,
        user_id: user?.id,
        metadata: {
          retirement_type: formData.type,
          contributions_ytd: formData.contributions_ytd,
          employer_match: formData.employer_match,
          investment_strategy: formData.investment_strategy,
          target_retirement_date: formData.target_retirement_date,
          beneficiaries: formData.beneficiaries,
          preservation_age: formData.preservation_age,
          tax_file_number: formData.tax_file_number,
          member_number: formData.member_number,
          insurance_cover: formData.insurance_cover,
          investment_options: formData.investment_options,
          fees: formData.fees
        }
      };

      const { data, error } = await supabase
        .from('financial_accounts')
        .insert([accountData])
        .select()
        .single();

      if (error) throw error;

      setAccounts(prev => [...prev, data]);
      setShowAddModal(false);
      setFormData({
        type: 'superannuation',
        provider: '',
        account_number: '',
        balance: '',
        contributions_ytd: '',
        employer_match: '',
        investment_strategy: '',
        target_retirement_date: '',
        beneficiaries: [''],
        notes: '',
        preservation_age: '',
        tax_file_number: '',
        member_number: '',
        insurance_cover: {
          death: '',
          tpd: '',
          income_protection: ''
        },
        investment_options: [{ name: '', percentage: '' }],
        fees: {
          admin_fee: '',
          investment_fee: '',
          insurance_fee: ''
        }
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save retirement account');
    }
  };

  const handleDeleteAccount = async (id: string) => {
    try {
      const { error } = await supabase
        .from('financial_accounts')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setAccounts(prev => prev.filter(account => account.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete retirement account');
    }
  };

  const calculateTotalBalance = () => {
    return accounts.reduce((total, account) => {
      const balance = parseFloat(account.balance || '0');
      return total + balance;
    }, 0);
  };

  const calculateTotalContributions = () => {
    return accounts.reduce((total, account) => {
      const contributions = parseFloat(account.contributions_ytd || '0');
      return total + contributions;
    }, 0);
  };

  const filteredAccounts = accounts.filter(account => 
    account.provider.toLowerCase().includes(searchQuery.toLowerCase()) ||
    account.type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-idecide-dark">Retirement Accounts</h1>
          <p className="text-gray-600 mt-1">Track your retirement savings and investments</p>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowAddModal(true)}
          icon={<Plus size={20} />}
        >
          Add Account
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-idecide-primary to-idecide-secondary text-idecide-dark">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-2">Total Balance</h3>
            <p className="text-3xl font-bold">${calculateTotalBalance().toLocaleString()}</p>
            <p className="text-sm mt-2 text-idecide-dark/80">Combined retirement savings</p>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-idecide-secondary to-idecide-primary text-idecide-dark">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-2">YTD Contributions</h3>
            <p className="text-3xl font-bold">${calculateTotalContributions().toLocaleString()}</p>
            <p className="text-sm mt-2 text-idecide-dark/80">Total contributions this year</p>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-idecide-primary to-idecide-secondary text-idecide-dark">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-2">Active Accounts</h3>
            <p className="text-3xl font-bold">{accounts.length}</p>
            <p className="text-sm mt-2 text-idecide-dark/80">Total retirement accounts</p>
          </div>
        </Card>
      </div>

      <div className="bg-white rounded-xl border border-idecide-primary shadow-sm">
        {/* Search Bar */}
        <div className="p-4 border-b border-idecide-primary/20">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search retirement accounts..."
            className="w-full"
          />
        </div>

        {/* Accounts List */}
        <div className="divide-y divide-idecide-primary/20">
          {filteredAccounts.map(account => (
            <div key={account.id} className="p-4 hover:bg-idecide-primary/5 transition-colors">
              <div className="flex justify-between items-start">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <PiggyBank className="text-idecide-primary" size={24} />
                    <div>
                      <h3 className="font-medium text-idecide-dark">{account.provider}</h3>
                      <p className="text-sm text-idecide-dark/70 capitalize">{account.type}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <p className="text-sm text-idecide-dark/70">Current Balance</p>
                      <p className="font-medium text-idecide-dark">${parseFloat(account.balance).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-idecide-dark/70">YTD Contributions</p>
                      <p className="font-medium text-idecide-dark">${parseFloat(account.contributions_ytd).toLocaleString()}</p>
                    </div>
                  </div>

                  {account.employer_match && (
                    <div className="flex items-center gap-1 text-idecide-dark/70">
                      <DollarSign size={16} />
                      <span>Employer Match: {account.employer_match}%</span>
                    </div>
                  )}

                  {account.investment_strategy && (
                    <p className="text-sm text-idecide-dark/70">
                      Strategy: {account.investment_strategy}
                    </p>
                  )}

                  {account.target_retirement_date && (
                    <p className="text-sm text-idecide-dark/70">
                      Target Retirement: {new Date(account.target_retirement_date).getFullYear()}
                    </p>
                  )}

                  {account.preservation_age && (
                    <p className="text-sm text-idecide-dark/70">
                      Preservation Age: {account.preservation_age}
                    </p>
                  )}

                  {account.insurance_cover && (
                    <div className="mt-2 space-y-1">
                      <p className="text-sm font-medium text-idecide-dark">Insurance Cover:</p>
                      {account.insurance_cover.death && (
                        <p className="text-sm text-idecide-dark/70">Death: ${account.insurance_cover.death}</p>
                      )}
                      {account.insurance_cover.tpd && (
                        <p className="text-sm text-idecide-dark/70">TPD: ${account.insurance_cover.tpd}</p>
                      )}
                      {account.insurance_cover.income_protection && (
                        <p className="text-sm text-idecide-dark/70">Income Protection: ${account.insurance_cover.income_protection}</p>
                      )}
                    </div>
                  )}

                  {account.investment_options && account.investment_options.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm font-medium text-idecide-dark">Investment Options:</p>
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        {account.investment_options.map((option, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-idecide-primary" />
                            <span className="text-sm text-idecide-dark/70">{option.name}: {option.percentage}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {account.fees && (
                    <div className="mt-2 space-y-1">
                      <p className="text-sm font-medium text-idecide-dark">Fees:</p>
                      {account.fees.admin_fee && (
                        <p className="text-sm text-idecide-dark/70">Admin Fee: {account.fees.admin_fee}%</p>
                      )}
                      {account.fees.investment_fee && (
                        <p className="text-sm text-idecide-dark/70">Investment Fee: {account.fees.investment_fee}%</p>
                      )}
                      {account.fees.insurance_fee && (
                        <p className="text-sm text-idecide-dark/70">Insurance Fee: {account.fees.insurance_fee}%</p>
                      )}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => handleDeleteAccount(account.id)}
                  className="text-idecide-dark/40 hover:text-idecide-error transition-colors"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
          ))}

          {filteredAccounts.length === 0 && (
            <div className="p-8 text-center text-idecide-dark/70">
              {searchQuery
                ? 'No accounts match your search'
                : 'No retirement accounts added yet'}
            </div>
          )}
        </div>
      </div>

      {/* Add Account Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Retirement Account"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-idecide-dark mb-1">
              Account Type
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
            >
              {accountTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-idecide-dark mb-1">
              Provider
            </label>
            <input
              type="text"
              value={formData.provider}
              onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
              className="w-full p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
              placeholder="e.g., AustralianSuper, Vanguard"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-idecide-dark mb-1">
                Account Number
              </label>
              <input
                type="text"
                value={formData.account_number}
                onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                className="w-full p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-idecide-dark mb-1">
                Member Number
              </label>
              <input
                type="text"
                value={formData.member_number}
                onChange={(e) => setFormData({ ...formData, member_number: e.target.value })}
                className="w-full p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-idecide-dark mb-1">
                Current Balance
              </label>
              <input
                type="text"
                value={formData.balance}
                onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
                className="w-full p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
                placeholder="$"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-idecide-dark mb-1">
                YTD Contributions
              </label>
              <input
                type="text"
                value={formData.contributions_ytd}
                onChange={(e) => setFormData({ ...formData, contributions_ytd: e.target.value })}
                className="w-full p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
                placeholder="$"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-idecide-dark mb-1">
              Employer Match
            </label>
            <input
              type="text"
              value={formData.employer_match}
              onChange={(e) => setFormData({ ...formData, employer_match: e.target.value })}
              className="w-full p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
              placeholder="%"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-idecide-dark mb-1">
              Investment Strategy
            </label>
            <input
              type="text"
              value={formData.investment_strategy}
              onChange={(e) => setFormData({ ...formData, investment_strategy: e.target.value })}
              className="w-full p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
              placeholder="e.g., Balanced, Growth, Conservative"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-idecide-dark mb-1">
              Target Retirement Date
            </label>
            <input
              type="date"
              value={formData.target_retirement_date}
              onChange={(e) => setFormData({ ...formData, target_retirement_date: e.target.value })}
              className="w-full p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-idecide-dark mb-1">
              Preservation Age
            </label>
            <input
              type="text"
              value={formData.preservation_age}
              onChange={(e) => setFormData({ ...formData, preservation_age: e.target.value })}
              className="w-full p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
              placeholder="e.g., 60"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-idecide-dark mb-1">
              Tax File Number
            </label>
            <input
              type="text"
              value={formData.tax_file_number}
              onChange={(e) => setFormData({ ...formData, tax_file_number: e.target.value })}
              className="w-full p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-idecide-dark mb-1">
              Insurance Cover
            </label>
            <div className="space-y-2">
              <input
                type="text"
                value={formData.insurance_cover.death}
                onChange={(e) => setFormData({
                  ...formData,
                  insurance_cover: { ...formData.insurance_cover, death: e.target.value }
                })}
                className="w-full p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
                placeholder="Death Cover Amount ($)"
              />
              <input
                type="text"
                value={formData.insurance_cover.tpd}
                onChange={(e) => setFormData({
                  ...formData,
                  insurance_cover: { ...formData.insurance_cover, tpd: e.target.value }
                })}
                className="w-full p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
                placeholder="TPD Cover Amount ($)"
              />
              <input
                type="text"
                value={formData.insurance_cover.income_protection}
                onChange={(e) => setFormData({
                  ...formData,
                  insurance_cover: { ...formData.insurance_cover, income_protection: e.target.value }
                })}
                className="w-full p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
                placeholder="Income Protection Amount ($)"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-idecide-dark mb-1">
              Investment Options
            </label>
            {formData.investment_options.map((option, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={option.name}
                  onChange={(e) => {
                    const newOptions = [...formData.investment_options];
                    newOptions[index] = { ...option, name: e.target.value };
                    setFormData({ ...formData, investment_options: newOptions });
                  }}
                  className="flex-1 p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
                  placeholder="Option name"
                />
                <input
                  type="text"
                  value={option.percentage}
                  onChange={(e) => {
                    const newOptions = [...formData.investment_options];
                    newOptions[index] = { ...option, percentage: e.target.value };
                    setFormData({ ...formData, investment_options: newOptions });
                  }}
                  className="w-24 p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
                  placeholder="%"
                />
                <button
                  onClick={() => {
                    const newOptions = formData.investment_options.filter((_, i) => i !== index);
                    setFormData({ ...formData, investment_options: newOptions });
                  }}
                  className="text-idecide-error hover:text-idecide-error/80"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            <button
              onClick={() => setFormData({
                ...formData,
                investment_options: [...formData.investment_options, { name: '', percentage: '' }]
              })}
              className="text-idecide-primary hover:text-idecide-primary-hover text-sm"
            >
              Add Investment Option
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-idecide-dark mb-1">
              Fees
            </label>
            <div className="space-y-2">
              <input
                type="text"
                value={formData.fees.admin_fee}
                onChange={(e) => setFormData({
                  ...formData,
                  fees: { ...formData.fees, admin_fee: e.target.value }
                })}
                className="w-full p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
                placeholder="Admin Fee (%)"
              />
              <input
                type="text"
                value={formData.fees.investment_fee}
                onChange={(e) => setFormData({
                  ...formData,
                  fees: { ...formData.fees, investment_fee: e.target.value }
                })}
                className="w-full p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
                placeholder="Investment Fee (%)"
              />
              <input
                type="text"
                value={formData.fees.insurance_fee}
                onChange={(e) => setFormData({
                  ...formData,
                  fees: { ...formData.fees, insurance_fee: e.target.value }
                })}
                className="w-full p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
                placeholder="Insurance Fee (%)"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-idecide-dark mb-1">
              Beneficiaries
            </label>
            {formData.beneficiaries.map((beneficiary, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={beneficiary}
                  onChange={(e) => {
                    const newBeneficiaries = [...formData.beneficiaries];
                    newBeneficiaries[index] = e.target.value;
                    setFormData({ ...formData, beneficiaries: newBeneficiaries });
                  }}
                  className="flex-1 p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
                  placeholder="Full name"
                />
                <button
                  onClick={() => {
                    const newBeneficiaries = formData.beneficiaries.filter((_, i) => i !== index);
                    setFormData({ ...formData, beneficiaries: newBeneficiaries });
                  }}
                  className="text-idecide-error hover:text-idecide-error/80"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            <button
              onClick={() => setFormData({
                ...formData,
                beneficiaries: [...formData.beneficiaries, '']
              })}
              className="text-idecide-primary hover:text-idecide-primary-hover text-sm"
            >
              Add Beneficiary
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-idecide-dark mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full p-2 border border-idecide-primary rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAddModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveAccount}
            >
              Save Account
            </Button>
          </div>
        </div>
      </Modal>

      {error && (
        <div className="fixed bottom-4 right-4 bg-idecide-error/10 border border-idecide-error text-idecide-error px-4 py-3 rounded flex items-center gap-2">
          <AlertCircle size={20} />
          <p>{error}</p>
        </div>
      )}
    </div>
  );
}